// AdminSendStudentInvitations.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Paper,
  Chip,
  CircularProgress,
  Autocomplete,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  IconButton,
  Tabs,
  Tab,
  Tooltip,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HistoryIcon from '@mui/icons-material/History';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { AgGridReact } from 'ag-grid-react';
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
import api from '../../../API/axiosInstance';
import { API_ENDPOINTS } from '../../../config/api';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import safeToast from 'utils/safeToast';
import LoadingSkeleton from 'components/LoadingSkeleton';
import { adminStyles, loadingConfig } from 'styles/adminStyles';

ModuleRegistry.registerModules([AllCommunityModule]);
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

const AdminSendStudentInvitations = ({ open, onClose }) => {
  const gridRef = useRef(null);
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [invitationSent, setInvitationSent] = useState(null); // null = all, false = not sent, true = sent
  const [activeTab, setActiveTab] = useState('currentStudents'); // 'currentStudents' or 'alumni'
  const [registrationType, setRegistrationType] = useState(0); // 0 = All, 1 = Registered, 2 = Un-registered (for Alumni tab)
  const [selectedAlumni, setSelectedAlumni] = useState([]);
  const [sendingInvitations, setSendingInvitations] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  
  // Message logs state
  const [showLogs, setShowLogs] = useState(false);
  const [dateWiseLogs, setDateWiseLogs] = useState([]);
  const [selectedDateLogs, setSelectedDateLogs] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [loadingDateDetails, setLoadingDateDetails] = useState(false);
  const [dateFilter, setDateFilter] = useState({ fromDate: null, toDate: null });

  // Fetch templates
  const fetchTemplates = useCallback(async () => {
    try {
      setLoadingTemplates(true);
      const response = await api.get('/AdminMaster/get-all-templates');
      const data = Array.isArray(response.data) ? response.data : [];
      setTemplates(data);
      if (data.length > 0 && !selectedTemplate) {
        setSelectedTemplate(data[0]);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      safeToast.error('Failed to load templates');
    } finally {
      setLoadingTemplates(false);
    }
  }, [selectedTemplate]);

  const handleAlumniSelection = useCallback((alumni, checked) => {
    setSelectedAlumni(prev => {
      const identifier = alumni.uploadId || alumni.studentId;
      if (checked) {
        return prev.some(s => (s.uploadId || s.studentId) === identifier) ? prev : [...prev, alumni];
      } else {
        return prev.filter(s => (s.uploadId || s.studentId) !== identifier);
      }
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedAlumni.length === rowData.length) {
      setSelectedAlumni([]);
    } else {
      setSelectedAlumni([...rowData]);
    }
  }, [selectedAlumni.length, rowData]);

  const checkboxCellRenderer = useCallback((params) => {
    if (!params.data) return null;
    
    const alumni = params.data;
    const identifier = alumni.uploadId || alumni.studentId;
    const isSelected = selectedAlumni.some(s => (s.uploadId || s.studentId) === identifier);
    
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          width: '100%',
        }}
      >
        <input
          type="checkbox"
          checked={isSelected}
          onChange={(e) => {
            e.stopPropagation();
            handleAlumniSelection(alumni, e.target.checked);
          }}
          onClick={(e) => e.stopPropagation()}
          style={{
            width: '18px',
            height: '18px',
            cursor: 'pointer',
          }}
        />
      </Box>
    );
  }, [selectedAlumni, handleAlumniSelection]);

  // Text cell renderer for long text with ellipsis
  const textCellRenderer = useCallback((params) => {
    const value = params.value ?? "-";
    return (
      <span 
        title={value} 
        style={{ 
          display: "inline-block", 
          maxWidth: "100%", 
          overflow: "hidden", 
          textOverflow: "ellipsis", 
          whiteSpace: "nowrap",
          fontSize: "0.75rem"
        }}
      >
        {value}
      </span>
    );
  }, []);

  // Column definitions for Current Students tab - Simplified format (NO old format columns)
  const currentStudentsColumnDefs = useMemo(() => [
    {
      headerName: "S.No",
      valueGetter: (params) => {
        const api = params.api;
        const rowIdx = params.node ? params.node.rowIndex : 0;
        const hasPagination = !!api.paginationGetPageSize;
        if (hasPagination) {
          const page = api.paginationGetCurrentPage?.() ?? 0;
          const pageSize = api.paginationGetPageSize?.() ?? 0;
          return pageSize ? page * pageSize + rowIdx + 1 : rowIdx + 1;
        }
        return rowIdx + 1;
      },
      width: 55,
      minWidth: 50,
      maxWidth: 60,
      pinned: "left",
      resizable: false,
      sortable: false,
      filter: false,
      lockPosition: true,
      cellStyle: { 
        textAlign: "center",
        fontSize: "0.75rem",
        fontWeight: 600,
        color: '#344767'
      },
    },
    {
      field: "invitationSent",
      headerName: "Status",
      width: 100,
      minWidth: 90,
      pinned: "left",
      cellRenderer: (params) => {
        const sent = params.value;
        return (
          <Chip
            icon={sent ? <CheckCircleIcon /> : <CancelIcon />}
            label={sent ? 'Sent' : 'Pending'}
            size="small"
            color={sent ? 'success' : 'warning'}
            sx={{
              height: '24px',
              fontSize: '0.75rem',
              fontWeight: 600,
            }}
          />
        );
      },
      filter: "agTextColumnFilter",
      floatingFilter: true,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'equals',
      },
    },
    {
      field: "id",
      headerName: "Id",
      width: 80,
      minWidth: 70,
      filter: "agNumberColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
      },
    },
    {
      field: "rollNumber",
      headerName: "Roll Number",
      width: 100,
      minWidth: 90,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "regNumber",
      headerName: "Registration Number",
      width: 130,
      minWidth: 110,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "name",
      headerName: "Name",
      flex: 1.5,
      minWidth: 120,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      cellStyle: { fontWeight: 500 },
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "gender",
      headerName: "Gender",
      width: 80,
      minWidth: 70,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'equals',
      },
    },
    {
      field: "dateOfBirth",
      headerName: "Date of Birth",
      width: 100,
      minWidth: 90,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "maritalStatus",
      headerName: "Marital Status",
      width: 100,
      minWidth: 90,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'equals',
      },
    },
    {
      field: "batchYear",
      headerName: "Batch Year",
      width: 85,
      minWidth: 75,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'equals',
      },
    },
    {
      field: "collegeStudied",
      headerName: "College Studied",
      flex: 1.5,
      minWidth: 120,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "courseStudied",
      headerName: "Course Studied",
      flex: 1.5,
      minWidth: 120,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "branch",
      headerName: "Branch",
      flex: 1,
      minWidth: 90,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "contactNumber",
      headerName: "Contact Number",
      width: 110,
      minWidth: 100,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "whatsappNumber",
      headerName: "WhatsApp Number",
      width: 110,
      minWidth: 100,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "emailID",
      headerName: "Email ID",
      flex: 1.5,
      minWidth: 130,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "presentStatus",
      headerName: "Present Status",
      flex: 1,
      minWidth: 100,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "forOthers",
      headerName: "For Others",
      width: 100,
      minWidth: 90,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "organization",
      headerName: "Organization",
      flex: 1.5,
      minWidth: 120,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "designation",
      headerName: "Designation",
      flex: 1,
      minWidth: 100,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "location",
      headerName: "Location",
      flex: 1,
      minWidth: 90,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "availability",
      headerName: "Availability",
      flex: 1,
      minWidth: 100,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "feedback",
      headerName: "Feedback",
      flex: 2,
      minWidth: 120,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "nativePlace",
      headerName: "Native Place",
      flex: 1,
      minWidth: 100,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "domain",
      headerName: "Domain",
      width: 100,
      minWidth: 90,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "category",
      headerName: "Category",
      width: 100,
      minWidth: 90,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "firstName",
      headerName: "First Name",
      flex: 1,
      minWidth: 90,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "lastName",
      headerName: "Last Name",
      flex: 1,
      minWidth: 90,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1.5,
      minWidth: 130,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "mobile",
      headerName: "Mobile",
      width: 100,
      minWidth: 90,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "uploadedBy",
      headerName: "Uploaded By",
      width: 100,
      minWidth: 90,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "uploadedDate",
      headerName: "Uploaded Date",
      width: 110,
      minWidth: 100,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "isActive",
      headerName: "Is Active",
      width: 80,
      minWidth: 70,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellStyle: (params) => {
        return {
          fontSize: "0.75rem",
          ...(params.value === true || params.value === "Yes" 
            ? { color: "#4caf50", fontWeight: "bold" }
            : { color: "#f44336", fontWeight: "bold" })
        };
      },
      valueFormatter: (params) => {
        if (params.value === true || params.value === "Yes") return "Yes";
        return "No";
      },
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'equals',
      },
    },
  ], [textCellRenderer]);

  // Column definitions for Alumni tab - All fields from stored procedure
  const alumniColumnDefs = useMemo(() => [
    {
      headerName: "S.No",
      valueGetter: (params) => {
        const api = params.api;
        const rowIdx = params.node ? params.node.rowIndex : 0;
        const hasPagination = !!api.paginationGetPageSize;
        if (hasPagination) {
          const page = api.paginationGetCurrentPage?.() ?? 0;
          const pageSize = api.paginationGetPageSize?.() ?? 0;
          return pageSize ? page * pageSize + rowIdx + 1 : rowIdx + 1;
        }
        return rowIdx + 1;
      },
      width: 60,
      minWidth: 55,
      pinned: "left",
      resizable: false,
      sortable: false,
      filter: false,
      lockPosition: true,
      cellStyle: { 
        textAlign: "center",
        fontSize: "0.75rem",
        fontWeight: 600,
        color: '#344767'
      },
    },
    {
      field: "invitationSent",
      headerName: "Status",
      width: 100,
      minWidth: 90,
      pinned: "left",
      cellRenderer: (params) => {
        const sent = params.value;
        return (
          <Chip
            icon={sent ? <CheckCircleIcon /> : <CancelIcon />}
            label={sent ? 'Sent' : 'Pending'}
            size="small"
            color={sent ? 'success' : 'warning'}
            sx={{
              height: '24px',
              fontSize: '0.75rem',
              fontWeight: 600,
            }}
          />
        );
      },
      filter: "agTextColumnFilter",
      floatingFilter: true,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'equals',
      },
    },
    {
      field: "erpStudentId",
      headerName: "ERP ID",
      width: 90,
      minWidth: 80,
      filter: "agNumberColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
      },
    },
    {
      field: "rollNum",
      headerName: "Roll Number",
      width: 100,
      minWidth: 90,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "regNum",
      headerName: "Registration Number",
      width: 130,
      minWidth: 110,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "fullName",
      headerName: "Full Name",
      flex: 1.5,
      minWidth: 120,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      cellStyle: { fontWeight: 500 },
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "firstName",
      headerName: "First Name",
      flex: 1,
      minWidth: 90,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "lastName",
      headerName: "Last Name",
      flex: 1,
      minWidth: 90,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "mobile",
      headerName: "Mobile",
      width: 110,
      minWidth: 100,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "whatsAppNumber",
      headerName: "WhatsApp",
      width: 110,
      minWidth: 100,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "batchYear",
      headerName: "Batch",
      width: 85,
      minWidth: 75,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'equals',
      },
    },
    {
      field: "dateOfBirth",
      headerName: "Date of Birth",
      width: 110,
      minWidth: 100,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "gender",
      headerName: "Gender",
      width: 80,
      minWidth: 70,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'equals',
      },
    },
    {
      field: "maritalStatus",
      headerName: "Marital Status",
      width: 100,
      minWidth: 90,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'equals',
      },
    },
    {
      field: "collegeStudied",
      headerName: "College Studied",
      flex: 1.5,
      minWidth: 120,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "courseStudied",
      headerName: "Course Studied",
      flex: 1.5,
      minWidth: 120,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "branch",
      headerName: "Branch",
      flex: 1,
      minWidth: 90,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "category",
      headerName: "Category",
      width: 100,
      minWidth: 90,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "presentStatus",
      headerName: "Present Status",
      flex: 1,
      minWidth: 100,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "forOthers",
      headerName: "For Others",
      width: 100,
      minWidth: 90,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "domain",
      headerName: "Domain",
      width: 100,
      minWidth: 90,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "organization",
      headerName: "Organization",
      flex: 1.5,
      minWidth: 120,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "designation",
      headerName: "Designation",
      flex: 1,
      minWidth: 100,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "location",
      headerName: "Location",
      flex: 1,
      minWidth: 90,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "availability",
      headerName: "Availability",
      flex: 1,
      minWidth: 100,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "feedback",
      headerName: "Feedback",
      flex: 2,
      minWidth: 120,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "nativePlace",
      headerName: "Native Place",
      flex: 1,
      minWidth: 100,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "instype",
      headerName: "Inst. Type",
      width: 110,
      minWidth: 100,
      hide: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "intname",
      headerName: "Institute",
      flex: 1,
      minWidth: 100,
      hide: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "cat",
      headerName: "Category (Master)",
      width: 120,
      minWidth: 110,
      hide: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "dept",
      headerName: "Department",
      flex: 1,
      minWidth: 100,
      hide: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "deg",
      headerName: "Degree",
      width: 110,
      minWidth: 100,
      hide: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "coursename",
      headerName: "Course",
      flex: 1,
      minWidth: 100,
      hide: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "community",
      headerName: "Community",
      width: 110,
      minWidth: 100,
      hide: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "bloodgroup",
      headerName: "Blood Group",
      width: 110,
      minWidth: 100,
      hide: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "district",
      headerName: "District",
      width: 110,
      minWidth: 100,
      hide: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "socialMediaCount",
      headerName: "Social Media",
      width: 100,
      minWidth: 90,
      hide: true,
      filter: "agNumberColumnFilter",
      floatingFilter: true,
      cellStyle: { textAlign: "center" },
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
      },
    },
    {
      field: "expertiseCount",
      headerName: "Expertise",
      width: 90,
      minWidth: 80,
      hide: true,
      filter: "agNumberColumnFilter",
      floatingFilter: true,
      cellStyle: { textAlign: "center" },
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
      },
    },
    {
      field: "chapterCount",
      headerName: "Chapters",
      width: 90,
      minWidth: 80,
      hide: true,
      filter: "agNumberColumnFilter",
      floatingFilter: true,
      cellStyle: { textAlign: "center" },
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
      },
    },
    {
      field: "eventCount",
      headerName: "Events",
      width: 80,
      minWidth: 70,
      hide: true,
      filter: "agNumberColumnFilter",
      floatingFilter: true,
      cellStyle: { textAlign: "center" },
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
      },
    },
    {
      field: "careerPosts",
      headerName: "Career Posts",
      width: 100,
      minWidth: 90,
      hide: true,
      filter: "agNumberColumnFilter",
      floatingFilter: true,
      cellStyle: { textAlign: "center" },
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
      },
    },
    {
      field: "commentCount",
      headerName: "Comments",
      width: 90,
      minWidth: 80,
      hide: true,
      filter: "agNumberColumnFilter",
      floatingFilter: true,
      cellStyle: { textAlign: "center" },
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
      },
    },
    {
      field: "replyCount",
      headerName: "Replies",
      width: 80,
      minWidth: 70,
      hide: true,
      filter: "agNumberColumnFilter",
      floatingFilter: true,
      cellStyle: { textAlign: "center" },
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
      },
    },
  ], [textCellRenderer]);

  // Use appropriate column definitions based on active tab
  const columnDefs = useMemo(() => {
    return activeTab === 'currentStudents' ? currentStudentsColumnDefs : alumniColumnDefs;
  }, [activeTab, currentStudentsColumnDefs, alumniColumnDefs]);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    floatingFilter: true, // Enable floating filters for both tabs
    minWidth: 50,
    cellStyle: { fontSize: "0.75rem" },
    filterParams: {
      buttons: ['reset', 'apply'],
      closeOnApply: true,
    },
  }), []);

  const onGridReady = useCallback((params) => {
    gridRef.current = params.api;
  }, []);

  // Fetch CurrentStudentsUpload data - Simplified format
  const fetchCurrentStudents = useCallback(async (invitationSentFilter = null) => {
    try {
      setLoading(true);
      // Use the simplified endpoint that returns the new format
      const url = API_ENDPOINTS.STUDENT_REQUEST.GET_ALL_UPLOADED_STUDENTS(true);
      const response = await api.get(url);
      const data = Array.isArray(response.data) ? response.data : [];
      
      // Transform data for grid - Simplified format matching SQL query
      const formatDate = (dateValue) => {
        if (!dateValue) return "-";
        try {
          return new Date(dateValue).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          });
        } catch {
          return "-";
        }
      };

      const transformedData = data.map((item) => {
        // Filter by invitationSent if filter is set
        if (invitationSentFilter !== null && (item.invitationSent ?? false) !== invitationSentFilter) {
          return null;
        }

        return {
          uploadId: item.uploadId || item.UploadId,
          studentId: item.uploadId || item.UploadId, // Use UploadId as fallback
          id: item.id || item.Id || item.erpStudentId || item.ErpStudentId || "-",
          erpStudentId: item.erpStudentId || item.ErpStudentId || item.id || item.Id,
          rollNum: item.rollNum || item.RollNum || item.rollNumber || item.RollNumber || "-",
          regNum: item.regNum || item.RegNum || item.regNumber || item.RegNumber || "-",
          rollNumber: item.rollNumber || item.RollNumber || item.rollNum || item.RollNum || "-",
          regNumber: item.regNumber || item.RegNumber || item.regNum || item.RegNum || "-",
          name: item.name || item.Name || item.fullName || item.FullName || `${item.firstName || item.FirstName || ''} ${item.lastName || item.LastName || ''}`.trim() || "-",
          fullName: item.fullName || item.FullName || item.name || item.Name || `${item.firstName || item.FirstName || ''} ${item.lastName || item.LastName || ''}`.trim() || "-",
          firstName: item.firstName || item.FirstName || "-",
          lastName: item.lastName || item.LastName || "-",
          gender: item.gender || item.Gender || "-",
          dateOfBirth: formatDate(item.dateOfBirth || item.DateOfBirth),
          maritalStatus: item.maritalStatus || item.MaritalStatus || "-",
          batchYear: item.batchYear || item.BatchYear || "-",
          collegeStudied: item.collegeStudied || item.CollegeStudied || "-",
          courseStudied: item.courseStudied || item.CourseStudied || "-",
          branch: item.branch || item.Branch || "-",
          contactNumber: item.contactNumber || item.ContactNumber || "-",
          whatsappNumber: item.whatsappNumber || item.WhatsappNumber || item.whatsAppNumber || item.WhatsAppNumber || "-",
          whatsAppNumber: item.whatsappNumber || item.WhatsappNumber || item.whatsAppNumber || item.WhatsAppNumber || "-",
          emailID: item.emailID || item.EmailID || item.email || item.Email || "-",
          email: item.email || item.Email || item.emailID || item.EmailID || "-",
          mobile: item.mobile || item.Mobile || item.contactNumber || item.ContactNumber || "-",
          presentStatus: item.presentStatus || item.PresentStatus || "-",
          forOthers: item.forOthers || item.ForOthers || "-",
          organization: item.organization || item.Organization || "-",
          designation: item.designation || item.Designation || "-",
          location: item.location || item.Location || "-",
          availability: item.availability || item.Availability || "-",
          feedback: item.feedback || item.Feedback || "-",
          nativePlace: item.nativePlace || item.NativePlace || "-",
          domain: item.domain || item.Domain || "-",
          category: item.category || item.Category || "-",
          uploadedBy: item.uploadedBy || item.UploadedBy || "-",
          uploadedDate: formatDate(item.uploadedDate || item.UploadedDate),
          isActive: item.isActive ?? item.IsActive,
          invitationSent: item.invitationSent ?? item.InvitationSent ?? false,
        };
      }).filter(item => item !== null); // Remove filtered out items
      
      setRowData(transformedData);
    } catch (error) {
      console.error("Error fetching current students:", error);
      setRowData([]);
      safeToast.error('Failed to load current students data');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch Alumni data from StudentInfo (new flow)
  const fetchAlumni = useCallback(async (regType = 0) => {
    try {
      setLoading(true);
      
      const response = await api.post(API_ENDPOINTS.ADMIN.GET_ALUMNUS_DETAIL_INVITATION, {
        RegistedType: regType
      });
      const data = Array.isArray(response.data) ? response.data : [];
      
      // Transform data for grid (convert PascalCase to camelCase and handle uppercase keys)
      const formatDate = (dateValue) => {
        if (!dateValue) return "-";
        try {
          // Handle both date strings and Date objects
          const date = typeof dateValue === 'string' ? new Date(dateValue) : dateValue;
          return date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          });
        } catch {
          return dateValue || "-";
        }
      };

      const transformedData = data.map(alumni => {
        const convertKeys = (obj) => {
          const converted = {};
          for (const key in obj) {
            // Handle all uppercase keys (like INSTYPE, INTNAME) - keep as lowercase
            let camelKey;
            if (key === key.toUpperCase() && key.length > 1) {
              // All uppercase - convert to lowercase
              camelKey = key.toLowerCase();
            } else {
              // PascalCase - convert to camelCase
              camelKey = key.charAt(0).toLowerCase() + key.slice(1);
            }
            converted[camelKey] = obj[key];
          }
          return converted;
        };
        const converted = convertKeys(alumni);
        return {
          studentId: converted.studentId || 0,
          uploadId: converted.uploadId || converted.studentId || 0, // Use StudentId as fallback
          erpStudentId: converted.erpStudentId || 0,
          rollNum: converted.rollNum || converted.rollnum || '',
          regNum: converted.regNum || '',
          firstName: converted.firstName || '',
          lastName: converted.lastName || '',
          fullName: converted.fullName || `${converted.firstName || ''} ${converted.lastName || ''}`.trim() || '-',
          selfMobile: converted.selfMobile || '',
          mobile: converted.selfMobile || converted.mobile || '',
          whatsAppNumber: converted.customWhatsappNumber || converted.whatsappNumber || converted.whatsAppNumber || converted.selfMobile || '',
          customBatch: converted.customBatch || '',
          batchYear: converted.customBatch || converted.batchYear || '',
          deptId: converted.deptId || 0,
          courseId: converted.courseId || 0,
          customDob: converted.customDob || converted.doBStr || '',
          dateOfBirth: formatDate(converted.customDob || converted.doBStr),
          // Custom fields from stored procedure
          customGender: converted.customGender || '',
          gender: converted.gender || converted.customGender || '',
          customMaritalStatus: converted.customMaritalStatus || '',
          maritalStatus: converted.customMaritalStatus || '',
          customCollegeStudied: converted.customCollegeStudied || '',
          collegeStudied: converted.customCollegeStudied || '',
          customCourseStudied: converted.customCourseStudied || '',
          courseStudied: converted.customCourseStudied || '',
          customBranch: converted.customBranch || '',
          branch: converted.customBranch || '',
          customCategory: converted.customCategory || '',
          category: converted.customCategory || '',
          customPresentStatus: converted.customPresentStatus || '',
          presentStatus: converted.customPresentStatus || '',
          customForOthers: converted.customForOthers || '',
          forOthers: converted.customForOthers || '',
          domain: converted.domain || '',
          customOrganization: converted.customOrganization || '',
          organization: converted.customOrganization || '',
          customDesignation: converted.customDesignation || '',
          designation: converted.customDesignation || '',
          customLocation: converted.customLocation || '',
          location: converted.customLocation || '',
          customAvailability: converted.customAvailability || '',
          availability: converted.customAvailability || '',
          customFeedbackInput: converted.customFeedbackInput || '',
          feedback: converted.customFeedbackInput || '',
          customNativePlace: converted.customNativePlace || '',
          nativePlace: converted.customNativePlace || '',
          img: converted.img || '',
          // Count fields
          socialMediaCount: converted.socialMediaCount || 0,
          expertiseCount: converted.expertiseCount || 0,
          chapterCount: converted.chapterCount || 0,
          eventCount: converted.eventCount || 0,
          careerPosts: converted.careerPosts || 0,
          commentCount: converted.commentCount || 0,
          replyCount: converted.replyCount || 0,
          // Master data fields (if returned by stored procedure)
          instype: converted.instype || converted.INSTYPE || '',
          intname: converted.intname || converted.INTNAME || '',
          cat: converted.cat || converted.CAT || '',
          dept: converted.dept || converted.DEPT || '',
          deg: converted.deg || converted.DEG || '',
          coursename: converted.coursename || converted.COURSENAME || '',
          community: converted.community || converted.COMMUNITY || '',
          bloodgroup: converted.bloodgroup || converted.BLOODGROUP || '',
          district: converted.district || converted.DISTRICT || '',
          invitationSent: converted.invitationSent || false,
        };
      });
      
      setRowData(transformedData);
    } catch (error) {
      console.error("Error fetching alumni:", error);
      setRowData([]);
      safeToast.error('Failed to load alumni data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      if (activeTab === 'currentStudents') {
        fetchCurrentStudents(invitationSent);
      } else {
        fetchAlumni(registrationType);
      }
      fetchTemplates();
    }
  }, [open, activeTab, invitationSent, registrationType, fetchCurrentStudents, fetchAlumni, fetchTemplates]);

  const handleSendInvitations = useCallback(async (sendToAll = false) => {
    const alumniToSend = sendToAll ? rowData : selectedAlumni;
    
    if (alumniToSend.length === 0) {
      safeToast.warn('Please select at least one Alumni to send invitations.');
      return;
    }

    if (!selectedTemplate) {
      safeToast.warn('Please select a template before sending invitations.');
      return;
    }

    setSendingInvitations(true);
    try {
      // Prepare request with template information
      // Priority: ProviderTemplateName > TemplateName (for WhatsApp provider)
      const providerTemplateName = selectedTemplate.providerTemplateName || 
                                   selectedTemplate.templateName || 
                                   selectedTemplate.name;
      
      // Determine if we're sending to CurrentStudentsUpload or StudentInfo
      const isCurrentStudentsTab = activeTab === 'currentStudents';
      
      const requestPayload = {
        uploadIds: isCurrentStudentsTab ? alumniToSend.map(s => s.uploadId) : [],
        studentIds: !isCurrentStudentsTab ? alumniToSend.map(s => s.studentId || s.uploadId) : [],
        templateId: selectedTemplate.templateId || selectedTemplate.id || null,
        providerTemplateName: providerTemplateName,
        sentBy: 1 // TODO: Get from auth context
      };

      const response = await api.post(API_ENDPOINTS.WHATSAPP.SEND_INVITATIONS, requestPayload);

      if (response.data) {
        const result = response.data;
        if (result.success) {
          safeToast.success(
            `WhatsApp invitations sent successfully! ${result.totalSent} sent, ${result.totalFailed} failed.`
          );
        } else {
          safeToast.warn(
            `Some invitations failed. ${result.totalSent} sent, ${result.totalFailed} failed.`
          );
        }
        
        // Show detailed results if available
        if (result.results && result.results.length > 0) {
          const failed = result.results.filter(r => !r.success);
          if (failed.length > 0) {
            console.warn('Failed invitations:', failed);
          }
        }
        
        setSelectedAlumni([]);
        // Refresh list based on active tab
        if (activeTab === 'currentStudents') {
          fetchCurrentStudents(invitationSent);
        } else {
          fetchAlumni(registrationType);
        }
      }
    } catch (error) {
      console.error('Error sending WhatsApp invitations:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to send invitations. Please try again.';
      safeToast.error(errorMessage);
    } finally {
      setSendingInvitations(false);
    }
  }, [selectedAlumni, rowData, selectedTemplate, activeTab, invitationSent, registrationType, fetchCurrentStudents, fetchAlumni]);

  // Fetch date-wise message logs
  const fetchDateWiseLogs = useCallback(async (fromDate = null, toDate = null) => {
    try {
      setLoadingLogs(true);
      const params = {};
      if (fromDate) params.fromDate = fromDate.toISOString().split('T')[0];
      if (toDate) params.toDate = toDate.toISOString().split('T')[0];
      
      const response = await api.get(API_ENDPOINTS.WHATSAPP.GET_DATEWISE_LOGS, { params });
      const data = Array.isArray(response.data) ? response.data : [];
      setDateWiseLogs(data);
    } catch (error) {
      console.error('Error fetching date-wise logs:', error);
      safeToast.error('Failed to load message logs');
      setDateWiseLogs([]);
    } finally {
      setLoadingLogs(false);
    }
  }, []);

  // Fetch detailed logs for a specific date
  const fetchLogsByDate = useCallback(async (date) => {
    try {
      setLoadingDateDetails(true);
      const params = { sendDate: date.toISOString().split('T')[0] };
      
      const response = await api.get(API_ENDPOINTS.WHATSAPP.GET_LOGS_BY_DATE, { params });
      const data = Array.isArray(response.data) ? response.data : [];
      setSelectedDateLogs(data);
      setSelectedDate(date);
    } catch (error) {
      console.error('Error fetching logs by date:', error);
      safeToast.error('Failed to load detailed logs');
      setSelectedDateLogs([]);
    } finally {
      setLoadingDateDetails(false);
    }
  }, []);

  // Toggle logs view
  const handleToggleLogs = useCallback(() => {
    setShowLogs(!showLogs);
    if (!showLogs) {
      // Set default date range (last 30 days)
      const toDate = new Date();
      const fromDate = new Date();
      fromDate.setDate(fromDate.getDate() - 30);
      setDateFilter({ fromDate, toDate });
      fetchDateWiseLogs(fromDate, toDate);
    }
  }, [showLogs, fetchDateWiseLogs]);

  const exportCsv = useCallback(() => {
    if (!rowData || rowData.length === 0) {
      safeToast.warn('No data to export');
      return;
    }

    try {
      const headers = ['S.No', 'Alumni Name', 'Email', 'Mobile', 'WhatsApp', 'Batch', 'Inst. Type', 'Institute', 'Category', 'Department', 'Degree', 'Course', 'Gender', 'Community', 'Blood Group', 'DOB', 'Country', 'State', 'District', 'Invitation Status'];
      const csvContent = [
        headers.join(','),
        ...rowData.map((row, index) => [
          index + 1,
          `"${(row.fullName || '').replace(/"/g, '""')}"`,
          `"${(row.email || '').replace(/"/g, '""')}"`,
          `"${(row.mobile || '').replace(/"/g, '""')}"`,
          `"${(row.whatsAppNumber || '').replace(/"/g, '""')}"`,
          `"${(row.batchYear || '').replace(/"/g, '""')}"`,
          `"${(row.instype || '').replace(/"/g, '""')}"`,
          `"${(row.intname || '').replace(/"/g, '""')}"`,
          `"${(row.cat || '').replace(/"/g, '""')}"`,
          `"${(row.dept || '').replace(/"/g, '""')}"`,
          `"${(row.deg || '').replace(/"/g, '""')}"`,
          `"${(row.coursename || '').replace(/"/g, '""')}"`,
          `"${(row.gender || '').replace(/"/g, '""')}"`,
          `"${(row.community || '').replace(/"/g, '""')}"`,
          `"${(row.bloodgroup || '').replace(/"/g, '""')}"`,
          `"${(row.dateOfBirth ? new Date(row.dateOfBirth).toLocaleDateString('en-GB') : '').replace(/"/g, '""')}"`,
          `"${(row.country || '').replace(/"/g, '""')}"`,
          `"${(row.state || '').replace(/"/g, '""')}"`,
          `"${(row.district || '').replace(/"/g, '""')}"`,
          `"${row.invitationSent ? 'Sent' : 'Pending'}"`
        ].join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `alumni-invitations-${new Date().toISOString().slice(0, 10)}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      safeToast.success('CSV exported successfully!');
    } catch (error) {
      console.error('CSV export error:', error);
      safeToast.error('Failed to export CSV. Please try again.');
    }
  }, [rowData]);

  const stats = useMemo(() => {
    const total = rowData.length;
    const sent = rowData.filter(a => a.invitationSent).length;
    const pending = total - sent;
    return { total, sent, pending };
  }, [rowData]);

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
        <DialogContent>
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "400px" }}>
            <CircularProgress size={50} />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      fullWidth 
      maxWidth="xl" 
      fullScreen
      PaperProps={{
        sx: {
          background: '#ffffff',
        }
      }}
    >
      <DialogTitle
        sx={{
          ...adminStyles.dialogTitle,
          background: '#ffffff',
          borderBottom: '1px solid #e0e0e0',
          py: 2,
          px: 3,
        }}
      >
        <MDBox display="flex" alignItems="center" justifyContent="space-between">
          <MDTypography variant="h5" fontWeight="bold" color="dark">
            Send Alumni Invitations
          </MDTypography>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              '&:hover': {
                backgroundColor: '#f5f5f5',
              },
            }}
          >
            <CancelIcon />
          </IconButton>
        </MDBox>
      </DialogTitle>

      <DialogContent sx={adminStyles.container}>
        <MDBox display="flex" flexDirection="column" gap={2}>
          {/* Main Tabs: CurrentStudents and Alumni */}
          <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <Tabs
              value={activeTab}
              onChange={(event, newValue) => {
                setActiveTab(newValue);
                setSelectedAlumni([]); // Clear selection when switching tabs
                if (newValue === 'currentStudents') {
                  setInvitationSent(null); // Reset filter for CurrentStudents
                } else {
                  setRegistrationType(0); // Reset to All Alumni
                }
              }}
              sx={{
                borderBottom: '1px solid #e0e0e0',
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  minHeight: 48,
                },
              }}
            >
              <Tab label="Current Students" value="currentStudents" />
              <Tab label="All Alumni" value="alumni" />
            </Tabs>
            
            {/* Sub-tabs for Alumni */}
            {activeTab === 'alumni' && (
              <Box sx={{ borderTop: '1px solid #e0e0e0', px: 2, py: 1 }}>
                <Tabs
                  value={registrationType}
                  onChange={(event, newValue) => {
                    setRegistrationType(newValue);
                    setSelectedAlumni([]); // Clear selection when switching sub-tabs
                  }}
                  sx={{
                    minHeight: 40,
                    '& .MuiTab-root': {
                      textTransform: 'none',
                      fontWeight: 500,
                      minHeight: 40,
                      fontSize: '0.875rem',
                    },
                  }}
                >
                  <Tab label="All" value={0} />
                  <Tab label="Registered" value={1} />
                  <Tab label="Un-registered" value={2} />
                </Tabs>
              </Box>
            )}
          </Paper>

          {/* Simple Statistics */}
          <Box sx={adminStyles.statsBox}>
            <MDBox display="flex" alignItems="center" gap={1}>
              <MDTypography variant="caption" color="text" fontWeight={600}>
                Total:
              </MDTypography>
              <MDTypography variant="body2" fontWeight="bold" color="dark">
                {stats.total}
              </MDTypography>
            </MDBox>
            <Divider orientation="vertical" flexItem />
            <MDBox display="flex" alignItems="center" gap={1}>
              <MDTypography variant="caption" color="text" fontWeight={600}>
                Sent:
              </MDTypography>
              <MDTypography variant="body2" fontWeight="bold" color="success">
                {stats.sent}
              </MDTypography>
            </MDBox>
            <Divider orientation="vertical" flexItem />
            <MDBox display="flex" alignItems="center" gap={1}>
              <MDTypography variant="caption" color="text" fontWeight={600}>
                Pending:
              </MDTypography>
              <MDTypography variant="body2" fontWeight="bold" color="warning">
                {stats.pending}
              </MDTypography>
            </MDBox>
          </Box>

          {/* Controls Section */}
          <Paper elevation={0} sx={adminStyles.paper}>
            <MDBox display="flex" flexDirection="column" gap={2}>
              <MDBox display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1.5}>
                <MDBox display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                  {/* Template Selection */}
                  <Autocomplete
                    size="small"
                    sx={{ 
                      minWidth: 250,
                    }}
                    options={templates}
                    value={selectedTemplate}
                    onChange={(event, newValue) => {
                      setSelectedTemplate(newValue);
                    }}
                    getOptionLabel={(option) => option.templateName || option.name || 'Select Template'}
                    loading={loadingTemplates}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Template"
                        size="small"
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        <MDBox>
                          <MDTypography variant="body2" fontWeight={600}>
                            {option.templateName || option.name}
                          </MDTypography>
                          {option.providerTemplateName && (
                            <MDTypography variant="caption" color="text.secondary">
                              Provider: {option.providerTemplateName}
                            </MDTypography>
                          )}
                        </MDBox>
                      </Box>
                    )}
                  />

                  {/* Filter by Status - Only for CurrentStudents tab */}
                  {activeTab === 'currentStudents' && (
                    <Autocomplete
                      size="small"
                      sx={{ 
                        minWidth: 180,
                      }}
                      options={[
                        { value: null, label: 'All' },
                        { value: false, label: 'Pending' },
                        { value: true, label: 'Sent' }
                      ]}
                      value={{ value: invitationSent, label: invitationSent === null ? 'All' : invitationSent ? 'Sent' : 'Pending' }}
                      onChange={(event, newValue) => {
                        if (newValue) {
                          setInvitationSent(newValue.value);
                        }
                      }}
                      getOptionLabel={(option) => option.label}
                      isOptionEqualToValue={(option, value) => option.value === value.value}
                      renderInput={(params) => (
                        <TextField {...params} label="Status" size="small" />
                      )}
                    />
                  )}

                  {/* Export Button */}
                  <MDButton
                    variant="outlined"
                    color="dark"
                    size="small"
                    startIcon={<FileDownloadIcon />}
                    onClick={exportCsv}
                    disabled={rowData.length === 0}
                  >
                    Export
                  </MDButton>

                  {/* Message Logs Button */}
                  <MDButton
                    variant="outlined"
                    color="info"
                    size="small"
                    startIcon={<HistoryIcon />}
                    onClick={handleToggleLogs}
                  >
                    {showLogs ? 'Hide Logs' : 'Message Logs'}
                  </MDButton>
                </MDBox>

                {/* Action Buttons */}
                <MDBox display="flex" alignItems="center" gap={1}>
                  <MDButton
                    variant="outlined"
                    color="dark"
                    size="small"
                    onClick={handleSelectAll}
                  >
                    {selectedAlumni.length === rowData.length ? 'Deselect All' : 'Select All'}
                  </MDButton>

                  <MDButton
                    variant="gradient"
                    color="info"
                    size="small"
                    startIcon={<SendIcon />}
                    onClick={() => handleSendInvitations(false)}
                    disabled={selectedAlumni.length === 0 || sendingInvitations || !selectedTemplate}
                  >
                    {sendingInvitations 
                      ? 'Sending...' 
                      : `Send Selected (${selectedAlumni.length})`}
                  </MDButton>

                  <MDButton
                    variant="gradient"
                    color="success"
                    size="small"
                    startIcon={<SendIcon />}
                    onClick={() => handleSendInvitations(true)}
                    disabled={rowData.length === 0 || sendingInvitations || !selectedTemplate}
                  >
                    {sendingInvitations ? 'Sending...' : `Send All (${rowData.length})`}
                  </MDButton>
                </MDBox>
              </MDBox>

              {/* Selection Info */}
              {selectedAlumni.length > 0 && (
                <MDBox
                  sx={{
                    p: 1,
                    borderRadius: 1,
                    background: '#f8f9fa',
                    border: '1px solid #e9ecef',
                  }}
                >
                  <MDTypography variant="body2" color="text">
                    {selectedAlumni.length} Alumni selected
                  </MDTypography>
                </MDBox>
              )}
            </MDBox>
          </Paper>

          {/* Data Grid */}
          {loading ? (
            <LoadingSkeleton 
              variant="table" 
              rows={8} 
              showHeader={false}
              showStats={false}
              sx={{ mt: 2 }}
            />
          ) : (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 1,
                overflow: 'hidden',
                background: 'white',
                border: '1px solid #e0e0e0',
              }}
            >
              <div className="ag-theme-alpine" style={{ height: 'calc(100vh - 300px)', width: '100%' }}>
                <AgGridReact
                  ref={gridRef}
                  rowData={rowData}
                  columnDefs={[
                    {
                      headerName: "",
                      field: "checkbox",
                      width: 5,
                      pinned: "left",
                      cellRenderer: checkboxCellRenderer,
                      headerCheckboxSelection: false,
                      suppressSizeToFit: true,
                      sortable: false,
                      filter: false,
                      resizable: false,
                      lockPosition: true,
                      cellStyle: { 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        padding: 0,
                      },
                    },
                    ...columnDefs
                  ]}
                  defaultColDef={defaultColDef}
                  onGridReady={onGridReady}
                  animateRows={true}
                  suppressRowClickSelection={true}
                  rowHeight={activeTab === 'currentStudents' ? 28 : 40}
                  headerHeight={activeTab === 'currentStudents' ? 32 : 36}
                  floatingFiltersHeight={activeTab === 'currentStudents' ? 22 : undefined}
                  pagination={true}
                  paginationPageSize={activeTab === 'currentStudents' ? 30 : 25}
                  suppressSizeToFit={true}
                  rowSelection="multiple"
                  getRowId={(params) => params.data.uploadId || params.data.studentId}
                />
              </div>
              {activeTab === 'currentStudents' ? (
                <style>{`
                  .ag-theme-alpine {
                    --ag-header-background-color: #ffffff;
                    --ag-header-foreground-color: #344767;
                    --ag-border-color: #e0e0e0;
                    --ag-row-hover-color: #f5f5f5;
                    --ag-selected-row-background-color: #f0f0f0;
                  }
                  .ag-theme-alpine .ag-header-cell,
                  .ag-theme-alpine .ag-header-group-cell {
                    font-size: 0.75rem !important;
                    font-weight: 600;
                    padding: 4px 8px !important;
                  }
                  .ag-theme-alpine .ag-cell {
                    font-size: 0.75rem !important;
                    padding: 4px 8px !important;
                    line-height: 1.4;
                    color: #344767;
                  }
                  .ag-theme-alpine .ag-row {
                    min-height: 28px !important;
                    border-bottom: 1px solid #e0e0e0;
                  }
                  .ag-theme-alpine .ag-row:hover {
                    background-color: #f5f5f5 !important;
                  }
                  .ag-theme-alpine .ag-header-row {
                    min-height: 32px !important;
                    background: #ffffff;
                    border-bottom: 2px solid #e0e0e0;
                  }
                  .ag-theme-alpine .ag-header-row-filter {
                    min-height: 22px !important;
                  }
                  .ag-theme-alpine .ag-header-cell {
                    padding: 0;
                    border-right: 1px solid #e0e0e0;
                  }
                  .ag-theme-alpine .ag-cell-wrapper {
                    padding: 0;
                  }
                  .ag-theme-alpine .ag-cell[col-id="checkbox"] {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 0;
                  }
                  .ag-theme-alpine .ag-floating-filter-input,
                  .ag-theme-alpine .ag-floating-filter-body {
                    font-size: 0.65rem !important;
                    height: 20px !important;
                    min-height: 20px !important;
                  }
                  .ag-theme-alpine .ag-floating-filter-input input {
                    font-size: 0.65rem !important;
                    padding: 1px 3px !important;
                    height: 20px !important;
                    line-height: 20px !important;
                  }
                  .ag-theme-alpine .ag-floating-filter-wrapper {
                    height: 20px !important;
                    min-height: 20px !important;
                  }
                  .ag-theme-alpine .ag-header-cell-filtered {
                    background-color: #e3f2fd !important;
                  }
                  .ag-theme-alpine .ag-paging-panel {
                    font-size: 0.75rem !important;
                    padding: 4px 8px !important;
                  }
                `}</style>
              ) : (
                <style>{`
                  .ag-theme-alpine {
                    --ag-header-background-color: #ffffff;
                    --ag-header-foreground-color: #344767;
                    --ag-border-color: #e0e0e0;
                    --ag-row-hover-color: #f5f5f5;
                    --ag-selected-row-background-color: #f0f0f0;
                  }
                  .ag-theme-alpine .ag-header-cell-label {
                    font-size: 11px;
                    font-weight: 600;
                    padding: 6px 10px;
                    color: #344767;
                  }
                  .ag-theme-alpine .ag-cell {
                    font-size: 11px;
                    padding: 6px 10px;
                    line-height: 1.4;
                    color: #344767;
                  }
                  .ag-theme-alpine .ag-row {
                    min-height: 40px;
                    border-bottom: 1px solid #e0e0e0;
                  }
                  .ag-theme-alpine .ag-row:hover {
                    background-color: #f5f5f5 !important;
                  }
                  .ag-theme-alpine .ag-header-row {
                    min-height: 36px;
                    background: #ffffff;
                    border-bottom: 2px solid #e0e0e0;
                  }
                  .ag-theme-alpine .ag-header-cell {
                    padding: 0;
                    border-right: 1px solid #e0e0e0;
                  }
                  .ag-theme-alpine .ag-cell-wrapper {
                    padding: 0;
                  }
                  .ag-theme-alpine .ag-cell[col-id="checkbox"] {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 0;
                  }
                `}</style>
              )}
            </Paper>
          )}

          {/* Message Logs Section */}
          {showLogs && (
            <Paper elevation={0} sx={{ ...adminStyles.paper, mt: 2 }}>
              <MDBox display="flex" flexDirection="column" gap={2}>
                <MDBox display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
                  <MDTypography variant="h6" fontWeight="bold">
                    Message Logs - Date Wise
                  </MDTypography>
                  <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    <TextField
                      type="date"
                      label="From Date"
                      size="small"
                      value={dateFilter.fromDate ? dateFilter.fromDate.toISOString().split('T')[0] : ''}
                      onChange={(e) => {
                        const newFromDate = e.target.value ? new Date(e.target.value) : null;
                        setDateFilter(prev => ({ ...prev, fromDate: newFromDate }));
                        if (newFromDate && dateFilter.toDate) {
                          fetchDateWiseLogs(newFromDate, dateFilter.toDate);
                        }
                      }}
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: 150 }}
                    />
                    <TextField
                      type="date"
                      label="To Date"
                      size="small"
                      value={dateFilter.toDate ? dateFilter.toDate.toISOString().split('T')[0] : ''}
                      onChange={(e) => {
                        const newToDate = e.target.value ? new Date(e.target.value) : null;
                        setDateFilter(prev => ({ ...prev, toDate: newToDate }));
                        if (dateFilter.fromDate && newToDate) {
                          fetchDateWiseLogs(dateFilter.fromDate, newToDate);
                        }
                      }}
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: 150 }}
                    />
                    <MDButton
                      variant="outlined"
                      color="primary"
                      size="small"
                      onClick={() => {
                        if (dateFilter.fromDate && dateFilter.toDate) {
                          fetchDateWiseLogs(dateFilter.fromDate, dateFilter.toDate);
                        }
                      }}
                      disabled={loadingLogs}
                    >
                      {loadingLogs ? 'Loading...' : 'Refresh'}
                    </MDButton>
                  </MDBox>
                </MDBox>

                {loadingLogs ? (
                  <LoadingSkeleton variant="table" rows={5} showHeader={true} />
                ) : (
                  <>
                    {/* Date-wise Summary Grid */}
                    <div className="ag-theme-alpine" style={{ height: '400px', width: '100%' }}>
                      <AgGridReact
                        rowData={dateWiseLogs}
                        columnDefs={[
                          {
                            headerName: "Date",
                            field: "sendDate",
                            width: 120,
                            valueFormatter: (params) => {
                              if (!params.value) return '';
                              const date = new Date(params.value);
                              return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
                            },
                            cellStyle: { fontWeight: 600 },
                          },
                          {
                            headerName: "Total",
                            field: "totalMessages",
                            width: 80,
                            cellStyle: { textAlign: 'center', fontWeight: 600 },
                          },
                          {
                            headerName: "Success",
                            field: "successCount",
                            width: 80,
                            cellStyle: { textAlign: 'center', color: '#28a745', fontWeight: 600 },
                          },
                          {
                            headerName: "Failed",
                            field: "failedCount",
                            width: 80,
                            cellStyle: { textAlign: 'center', color: '#dc3545', fontWeight: 600 },
                          },
                          {
                            headerName: "Accepted",
                            field: "acceptedCount",
                            width: 90,
                            cellStyle: { textAlign: 'center' },
                          },
                          {
                            headerName: "Sent",
                            field: "sentCount",
                            width: 80,
                            cellStyle: { textAlign: 'center' },
                          },
                          {
                            headerName: "Delivered",
                            field: "deliveredCount",
                            width: 90,
                            cellStyle: { textAlign: 'center' },
                          },
                          {
                            headerName: "Read",
                            field: "readCount",
                            width: 80,
                            cellStyle: { textAlign: 'center' },
                          },
                          {
                            headerName: "Templates",
                            field: "uniqueTemplates",
                            width: 100,
                            cellStyle: { textAlign: 'center' },
                          },
                          {
                            headerName: "Contacts",
                            field: "uniqueContacts",
                            width: 100,
                            cellStyle: { textAlign: 'center' },
                          },
                          {
                            headerName: "Actions",
                            width: 120,
                            cellRenderer: (params) => {
                              const date = new Date(params.data.sendDate);
                              const isExpanded = selectedDate && 
                                selectedDate.toISOString().split('T')[0] === date.toISOString().split('T')[0];
                              return (
                                <MDButton
                                  size="small"
                                  variant="outlined"
                                  color="info"
                                  onClick={() => {
                                    if (isExpanded) {
                                      setSelectedDate(null);
                                      setSelectedDateLogs([]);
                                    } else {
                                      fetchLogsByDate(date);
                                    }
                                  }}
                                  disabled={loadingDateDetails}
                                >
                                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                  {isExpanded ? 'Hide' : 'Details'}
                                </MDButton>
                              );
                            },
                            cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
                          },
                        ]}
                        defaultColDef={{
                          sortable: true,
                          filter: true,
                          resizable: true,
                        }}
                        animateRows={true}
                        rowHeight={40}
                        headerHeight={36}
                      />
                    </div>

                    {/* Detailed Logs for Selected Date */}
                    {selectedDate && selectedDateLogs.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <MDTypography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                          Detailed Logs for {selectedDate.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                        </MDTypography>
                        {loadingDateDetails ? (
                          <LoadingSkeleton variant="table" rows={5} showHeader={true} />
                        ) : (
                          <div className="ag-theme-alpine" style={{ height: '400px', width: '100%' }}>
                            <AgGridReact
                              rowData={selectedDateLogs}
                              columnDefs={[
                                {
                                  headerName: "Contact",
                                  field: "contact",
                                  width: 120,
                                },
                                {
                                  headerName: "Alumni Name",
                                  field: "alumniName",
                                  width: 150,
                                  valueGetter: (params) => params.data.alumniName || params.data.uploadName || 'N/A',
                                },
                                {
                                  headerName: "Roll No",
                                  field: "rollNum",
                                  width: 100,
                                },
                                {
                                  headerName: "Status",
                                  field: "registrationStatus",
                                  width: 120,
                                  cellRenderer: (params) => {
                                    const status = params.value;
                                    const color = status === 'Registered' ? 'success' : status === 'Un-registered' ? 'warning' : 'info';
                                    return (
                                      <Chip
                                        label={status}
                                        size="small"
                                        color={color}
                                        sx={{ height: '24px', fontSize: '0.75rem' }}
                                      />
                                    );
                                  },
                                },
                                {
                                  headerName: "Template",
                                  field: "templateName",
                                  width: 150,
                                },
                                {
                                  headerName: "Message Status",
                                  field: "messageStatus",
                                  width: 120,
                                  cellRenderer: (params) => {
                                    const status = params.value;
                                    const color = status === 'accepted' || status === 'sent' || status === 'delivered' || status === 'read' 
                                      ? 'success' 
                                      : status === 'failed' 
                                      ? 'error' 
                                      : 'warning';
                                    return (
                                      <Chip
                                        label={status}
                                        size="small"
                                        color={color}
                                        sx={{ height: '24px', fontSize: '0.75rem' }}
                                      />
                                    );
                                  },
                                },
                                {
                                  headerName: "Sent By",
                                  field: "sentByName",
                                  width: 120,
                                },
                                {
                                  headerName: "Sent Time",
                                  field: "sentDate",
                                  width: 150,
                                  valueFormatter: (params) => {
                                    if (!params.value) return '';
                                    const date = new Date(params.value);
                                    return date.toLocaleString('en-GB');
                                  },
                                },
                                {
                                  headerName: "Error",
                                  field: "errorMessage",
                                  width: 200,
                                  cellRenderer: (params) => {
                                    if (!params.value) return '-';
                                    return (
                                      <Tooltip title={params.value}>
                                        <MDTypography variant="caption" sx={{ 
                                          overflow: 'hidden', 
                                          textOverflow: 'ellipsis', 
                                          whiteSpace: 'nowrap',
                                          maxWidth: '200px',
                                          color: 'error.main'
                                        }}>
                                          {params.value}
                                        </MDTypography>
                                      </Tooltip>
                                    );
                                  },
                                },
                              ]}
                              defaultColDef={{
                                sortable: true,
                                filter: true,
                                resizable: true,
                              }}
                              animateRows={true}
                              rowHeight={40}
                              headerHeight={36}
                            />
                          </div>
                        )}
                      </Box>
                    )}
                  </>
                )}
              </MDBox>
            </Paper>
          )}
        </MDBox>
      </DialogContent>

      <DialogActions
        sx={{
          px: 2.5,
          py: 1.5,
          background: '#ffffff',
          borderTop: '1px solid #e0e0e0',
        }}
      >
        <MDButton 
          onClick={onClose} 
          color="dark"
          variant="outlined"
          size="small"
        >
          Close
        </MDButton>
      </DialogActions>
    </Dialog>
  );
};

AdminSendStudentInvitations.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default AdminSendStudentInvitations;
